<template>
  <section>
    <div class="row">
      <div class="col">
        <p>This facility is used to push the ADAS Map Matrix items to selected destination ADAS Map applications.</p>
        <p>
          Note: when including Links, the process will take additional time. Also note, there will be no progress indicator as this is a background
          process.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <span>Select Items to Push:</span>
        <Multiselect
          ref="pushItems"
          v-model="pushItems"
          class="form-control custom-multiselect"
          placeholder="choose item(s) to push"
          :open-direction="'bottom'"
          :options="itemsToPush"
          mode="tags"
          label="name"
          value-prop="value"
          @deselect="selectedItems"
          @clear="selectedItems"
          @select="selectedItems"
        />
      </div>
      <div class="col">
        <span>Select Destination(s):</span>
        <Multiselect
          ref="apps"
          v-model="selectedApps"
          class="form-control custom-multiselect"
          placeholder="choose item(s) to push"
          :open-direction="'bottom'"
          :options="apps"
          mode="tags"
          label="appName"
          value-prop="appNumber"
        />
      </div>
      <div class="col-2">
        <button class="btn btn-primary custom-push-button" :disabled="!isSave" @click="push">Push Items</button>
      </div>
    </div>
    <div v-if="isKeywords" class="row mt-4">
      <div class="col">
        <p>
          <b>KEYWORDS:</b> This will push over the Vehicle Matrix based upon Keywords. You do have the option of pushing over a subset by make and
          year range. If you choose nothing, all items will be pushed.
        </p>
        <div class="row">
          <div class="col-5">
            <div class="input-group">
              <span class="input-group-text">Make</span>
              <select v-model="keywordMake" class="form-control" @change="changedKeywords">
                <option v-for="item of vehicles" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Year Start</span>
              <select v-model="yearStart" class="form-control" @change="changedKeywords">
                <option v-for="item of viewYears" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Year End</span>
              <select v-model="yearEnd" class="form-control" @change="changedKeywords">
                <option v-for="item of viewYears" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
            </div>
          </div>
          <div class="col">
            <span>Note: if you choose a Make here, the year range is <b>required</b>.</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLinks" class="row mt-3">
      <div class="col">
        <p>
          <b>LINKS:</b> This will push over the Vehicle Matrix Links. You are <u>required</u> to select the type, year, nad make here. Model is
          optional.
        </p>
        <div class="row">
          <div class="col-6">
            <div class="input-group">
              <span class="input-group-text">Type<span class="custom-required">*</span></span>
              <select v-model="lookupType" class="form-control" @change="changedLinks">
                <option v-for="item of lookupTypes" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Year<span class="custom-required">*</span></span>
              <select v-model="vehicleYear" class="form-control" @change="changedLinks">
                <option v-for="item of viewYears" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Make<span class="custom-required">*</span></span>
              <select v-model="vehicleMake" class="form-control" @change="changedLinks">
                <option v-for="item of vehicles" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span id="basic-addon1" class="input-group-text">Model</span>
              <input
                v-model="vehicleModel"
                type="text"
                class="form-control"
                style="width: 140px"
                placeholder="Model"
                aria-label="Model"
                @keyup="changedLinks"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-2">
        <button class="btn btn-primary me-2" :disabled="!isSave" @click="preview">Preview Differences</button>
        <a
          v-popover
          tab-index="0"
          data-bs-toggle="popover"
          data-bs-trigger="focus hover"
          data-bs-html="true"
          data-bs-sanitize="false"
          title="Information"
          :data-bs-content="`If there are a lot of differences, check existance of make/model and/or the year range.`"
          class="btn btn-link"
          role="button"
        >
          <i class="fas fa-info-circle"></i>
        </a>
        <div>
          <a v-show="isDifferences" href="#" @click="saveCsv">Save as CSV</a>
        </div>
      </div>
      <div class="col">
        <z-message v-model="message" :timeout="4000" class="alert alert-warning mt-2" />
        <span v-if="isPushed"
          >Matrix Items Push has been triggered. This is a background process; please allow time for the matrix to get to each application.</span
        >
      </div>
    </div>
    <div class="row mt-4">
      <div class="col">
        <p v-if="isAdasInfo">
          <b>NOTE:</b> You are pushing over ADAS Info items, if you've made any Inactive, you'll need to go to each ADAS Map site and update those
          same items as Inactive.
        </p>
        <p v-if="isLinks">
          <b>NOTE:</b> As a rule, you should push Keywords first, then Links. Keywords will create any new vehicle matrices for a given make and year
          range. Then the links can be pushed to those matrices.
        </p>
      </div>
    </div>
    <div v-if="isDifferences" class="row">
      <div v-show="loading.differences" class="col">
        <div class="custom-spinner">
          <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin />
        </div>
      </div>
      <div v-show="!loading.differences" class="col custom-differences">
        <table class="min-w-full table table-bordered table-striped">
          <!-- Dynamic Table Headers -->
          <thead class="bg-white table-sticky">
            <tr>
              <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
            </tr>
          </thead>
          <!-- Dynamic Table Rows -->
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(row, rowIndex) in differences" :key="rowIndex">
              <td v-for="(header, index) in headers" :key="index" v-html="calcValue(header, row[header])"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { adasmatrixStore } from '@/stores/adasmatrix'
import { appsStore } from '@/stores/apps'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Parser } from '@json2csv/plainjs'
import moment from 'moment'
import ZMessage from '@/components/utilities/elements/Message.vue'
import Multiselect from '@vueform/multiselect'
import globals from '@/plugins/globals'
export default {
  name: 'MatrixPush',
  components: {
    FontAwesomeIcon,
    ZMessage,
    Multiselect
  },
  data() {
    return {
      lookupTypes: [
        {
          id: 1,
          name: 'System',
          value: 1
        },
        {
          id: 2,
          name: 'Location',
          value: 2
        },
        {
          id: 3,
          name: 'Static',
          value: 3
        },
        {
          id: 4,
          name: 'Dynamic',
          value: 4
        }
      ],
      itemsToPush: [
        {
          name: 'Systems',
          value: 'Systems' // SystemType
        },
        {
          name: 'Locations',
          value: 'Locations' // LocationType
        },
        {
          name: 'Calibration Services',
          value: 'Services' // CalibrationType
        },
        {
          name: 'Exclusions',
          value: 'Exclusions' // Exclusion, ExclusionMake
        },
        {
          name: 'ADAS Info',
          value: 'InfoLookup' // AdasChecklistMatrixInfoLookup
        },
        {
          name: 'ADAS Matrix Categories and Subcategories',
          value: 'Matrix' // AdasChecklistMatrix, AdasChecklistMatrixItem, AdasChecklistMatrixLookup
        },
        {
          name: 'Keywords',
          value: 'Keywords' // AdasChecklistMatrixVehicle, AdasChecklistMatrixVehicleModel
        },
        {
          name: 'Links',
          value: 'Links' // AdasLink, AdasLinks, TriggerType
        },
        {
          name: 'Report Recommendations',
          value: 'Recommendations'
        },
        {
          name: 'No Scrub List',
          value: 'NoScrubList'
        },
        {
          name: 'Calibration Pricing',
          value: 'CalibrationPricing'
        }
      ],
      pushItems: [],
      selectedApps: [],
      isKeywords: false,
      isLinks: false,
      lookupType: null,
      vehicleYear: null,
      vehicleMake: null,
      vehicleModel: null,
      linkType: null,
      keywordMake: null,
      yearStart: null,
      yearEnd: null,
      message: null,
      isPushed: false,
      isAdasInfo: false
    }
  },
  mounted() {
    this.init()
  },
  computed: {
    ...mapState(appsStore, ['apps']),
    ...mapState(adasmatrixStore, ['loading', 'lookups', 'differences']),
    isSave() {
      let _is = false
      if (this.pushItems.length > 0 && this.selectedApps.length > 0) {
        _is = true
      }
      return _is
    },
    viewYears() {
      let _data = [
        {
          id: 0,
          name: null,
          value: null
        }
      ]
      let cnt = 1
      for (let idx = new Date().getFullYear() + 3; idx >= 2007; idx--) {
        _data.push({
          id: cnt,
          name: idx,
          value: idx
        })
        cnt++
      }
      return _data
    },
    vehicles() {
      let _data = []
      _data.push({
        id: -9999,
        name: 'all',
        value: -9999
      })
      if (this.lookups['VehicleMake']) {
        this.lookups['VehicleMake'].forEach((element) => {
          _data.push(element)
        })
      }
      return _data
    },
    isDifferences() {
      let _is = false
      if (this.differences) {
        if (this.differences.length > 0) {
          _is = true
        }
      }
      return _is
    },
    headers() {
      if (this.differences) {
        if (this.differences.length > 0) {
          return Object.keys(this.differences[0])
        }
      }
      return []
    }
  },
  methods: {
    ...mapActions(appsStore, ['getApps']),
    ...mapActions(adasmatrixStore, ['resetDifferences', 'pushMatrixItems', 'testMatrixItems', 'previewDifferences']),
    reset() {
      this.isPushed = false
      this.isKeywords = false
      this.isLinks = false
      this.isAdasInfo = false
      this.keywordMake = null
      this.yearStart = null
      this.yearEnd = null
      this.lookupType = null
      this.vehicleYear = null
      this.vehicleMake = null
      this.vehicleModel = null
      this.message = null
    },
    async init() {
      this.pushItems = []
      this.selectedApps = []
      this.resetDifferences()
      this.reset()
      await this.getApps('ADAS')
      await this.getApps('Connect', false)
    },
    changedLinks() {},
    changedKeywords() {},
    selectedItems() {
      this.reset()
      setTimeout(() => {
        console.log('selected items:', this.pushItems)
        if (this.pushItems.includes('Keywords')) {
          this.isKeywords = true
        }
        if (this.pushItems.includes('Links')) {
          this.isLinks = true
        }
        if (this.pushItems.includes('InfoLookup')) {
          this.isAdasInfo = true
        }
      }, 250)
    },
    async preview() {
      this.resetDifferences()
      let proceed = true
      if (this.pushItems.length !== 1) {
        this.message = 'You can only select one Item to preview.'
        proceed = false
      }
      if (this.selectedApps.length !== 1) {
        this.message = 'You can only select one Destination to preview.'
        proceed = false
      }
      if (this.isKeywords && this.keywordMake) {
        if (!this.yearStart || !this.yearEnd) {
          proceed = false
          this.message = 'For Keywords: when selecting vehicle, you must also select a Year Range to proceed.'
        }
      }
      if (this.isLinks) {
        if (!this.lookupType || !this.vehicleYear || !this.vehicleMake) {
          proceed = false
          this.message = 'For Links: at a minimum, you must select a Type, Year, and Make to proceed.'
        }
      }
      if (proceed) {
        let data = {
          item: this.pushItems[0],
          app: this.selectedApps[0],
          lookupType: this.isLinks ? this.lookupType : null,
          yearStart: this.isKeywords ? this.yearStart : this.isLinks ? this.vehicleYear : null,
          yearEnd: this.isKeywords ? this.yearEnd : null,
          vehicleMake: this.isKeywords ? this.keywordMake : this.isLinks ? this.vehicleMake : null,
          vehicleModel: this.isLinks ? this.vehicleModel : null
        }
        console.log('Calling Preview Differences:', {
          data: data,
          choice: this.pushItems,
          app: this.selectedApps
        })
        await this.previewDifferences(data)
      }
    },
    calcValue(header, value) {
      let results = ''
      if (value !== null) {
        if (globals.isObject(value) || Array.isArray(value)) {
          const json2csvParser = new Parser()
          let parsed = json2csvParser.parse(value)
          if (parsed.length > 150) {
            results = `<span>${parsed.replace(/[\n\r]/g, '<br/>').substring(0, 100)}...</span>`
          } else {
            results = `<span>${parsed.replace(/[\n\r]/g, '<br/>')}</span>`
          }
        } else {
          if (header.toUpperCase() === 'NAME' || header.toUpperCase() === 'SOURCE') {
            if (value === 'Both') {
              results = `<span class="text-success fw-bold">${value}</span>`
            } else if (value === 'LaunchPad') {
              results = `<span class="text-danger fw-bold fst-italic">${value}</span>`
            } else if (value === this.selectedApps[0]) {
              results = `<span class="text-danger fw-bold fst-italic">${value}</span>`
            } else {
              results = `<span class="text-danger fw-bold">${value}</span>`
            }
          } else {
            if (header !== 'Differences' && value.length > 150) {
              results = `<span>${value.substring(0, 100)}...</span>`
            } else if (header === 'price' && globals.isNumeric(value)) {
              results = `<span>$${parseFloat(value).toFixed(2)}</span>`
            } else {
              results = `<span>${value}</span>`
            }
          }
        }
      }
      return results
    },
    saveCsv() {
      var now = moment().format('MMDDYY')
      var fields = [...this.headers]
      var csvData = []

      if (!this.isKeywords) {
        csvData = this.differences
      } else {
        this.differences.forEach(x => {
          let data = {}
          fields.forEach(field => {
            if(field === 'AddlOperationRequired'){
              data[field] = x[field] === true ? 1 : 0
            } else if (field === 'AddlOperations') {
              let ops = x['AddlOperations'] || []
              if (ops.length > 0) {
                data[field]  = ops.map(op => `${op.isActive ? 1 : 0};${op.addlOperation}`).join('|')
              }
            } else {
              data[field] = x[field]
            }
          })
          csvData.push(data)
        })
      }
      const json2csvParser = new Parser({ fields })
      const csv = json2csvParser.parse(csvData)
      // download the file as CSV
      var downloadLink = document.createElement('a')
      var blob = new Blob(['\ufeff', csv])
      var url = URL.createObjectURL(blob)
      downloadLink.href = url
      downloadLink.download = `Preview ${this.pushItems[0]} data against ${this.selectedApps[0]} ${now}.csv`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },
    async push() {
      let proceed = true
      this.isPushed = false
      this.message = null
      if (this.isLinks) {
        if (!this.lookupType || !this.vehicleYear || !this.vehicleMake) {
          proceed = false
          this.message = 'For Links: at a minimum, you must select a Type, Year, and Make to proceed.'
        }
      }
      if (proceed) {
        let data = {
          items: this.pushItems,
          apps: this.selectedApps,
          keywords: {
            isPush: this.isKeywords,
            keywordMake: this.keywordMake,
            yearStart: this.yearStart,
            yearEnd: this.yearEnd
          },
          links: {
            isPush: this.isLinks,
            lookupType: this.lookupType,
            vehicleYear: this.vehicleYear,
            vehicleMake: this.vehicleMake,
            vehicleModel: this.vehicleModel
          }
        }
        console.log('pushing items:', data)
        // let results = await this.testMatrixItems(data)
        // console.log('Matrix Items Pushed:', results)
        await this.pushMatrixItems(data)
        this.pushItems = []
        this.selectedApps = []
        this.reset()
        this.resetDifferences()
        this.isPushed = true
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-required {
  color: red;
  position: relative;
  top: 0;
  right: 0;
}
.custom-spinner {
  width: 100%;
  text-align: center;
  font-size: 1.1em;
  min-height: 40px;
}
.custom-push-button {
  margin-top: 30px;
}
.custom-differences {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px;
}
</style>
